import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AuthProvider, useAuth } from './lib/auth.js';
import { LoginPage } from './routes/login.js';
import { RegisterPage } from './routes/register.js';
import { ForgotPasswordPage } from './routes/forgot-password.js';
import { ResetPasswordPage } from './routes/reset-password.js';
import { AppLayout } from './routes/_app.js';
import { DashboardPage } from './routes/_app/index.js';
import { AdminPage } from './routes/_app/admin.js';
import { AdminFeedbackPage } from './routes/_app/admin-feedback.js';
import { MembersPage } from './routes/_app/members.js';
import { ProfilePage } from './routes/_app/profile.js';
import { MatchesPage } from './routes/_app/matches.js';
import { MapPage } from './routes/_app/map.js';
import { SettingsPage } from './routes/_app/settings.js';
import { HelpPage } from './routes/_app/help.js';
import { BackupPage } from './routes/_app/backup.js';
import { MorePage } from './routes/_app/more.js';
import { JoinPage } from './routes/join.js';
import { NotFoundPage } from './routes/not-found.js';
import { GroupsPage } from './routes/_app/groups.js';
import { GroupDetailPage } from './routes/_app/group-detail.js';
import { GroupInvitePage } from './routes/group-invite.js';
import { api } from './lib/api.js';

const rootRoute = createRootRoute({
  component: () => (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  ),
  // Ohne das rendert der Router ein blankes "Not Found" — von dort kommt man
  // weder zurück zur Startseite noch zu Impressum/Datenschutz.
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // `next` trägt das Ziel nach dem Anmelden — etwa zurück zu einem Einladungslink.
  validateSearch: (search: Record<string, unknown>): { next?: string } => {
    const next = search.next;
    return typeof next === 'string' ? { next } : {};
  },
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  validateSearch: (search: Record<string, unknown>): { next?: string } => {
    const next = search.next;
    return typeof next === 'string' ? { next } : {};
  },
  component: RegisterPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

// Ziel der Links aus der Reset-Mail (?token=…).
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  beforeLoad: async () => {
    const me = await api.auth.me().catch(() => ({ user: null }));
    if (!me.user) throw redirect({ to: '/login' });
    return { user: me.user };
  },
  component: AppLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'app',
  component: DashboardPage,
});

const membersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'members',
  // `id` öffnet direkt die Detailansicht eines Kontakts — etwa aus einem Gruppenprofil heraus.
  validateSearch: (search: Record<string, unknown>): { id?: string } => {
    const id = search.id;
    return typeof id === 'string' && id ? { id } : {};
  },
  component: MembersPage,
});

const contactsImportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'contacts-import',
  beforeLoad: () => {
    throw redirect({ to: '/members' });
  },
});

const profileRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'profile',
  component: ProfilePage,
});

const searchRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'search',
  beforeLoad: () => {
    throw redirect({ to: '/members' });
  },
});

const matchesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'matches',
  component: MatchesPage,
});

const mapRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'map',
  component: MapPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'settings',
  component: SettingsPage,
});

const helpRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'help',
  component: HelpPage,
});

const backupRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'backup',
  component: BackupPage,
});

const moreRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'more',
  component: MorePage,
});

// Öffentliche Eintragungsseite — bewusst außerhalb von `_app`: kein Login, keine Sidebar.
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join/$slug',
  component: JoinPage,
});

const groupsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'groups',
  component: GroupsPage,
});

const groupDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'groups/$groupId',
  component: GroupDetailPage,
});

// Einladungslink einer Netzwerk-Gruppe — öffentlich, damit auch Leute ohne Konto
// sehen, wohin sie eingeladen werden, bevor sie sich anmelden.
const groupInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/g/$token',
  component: GroupInvitePage,
});

const adminRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'admin',
  beforeLoad: ({ context }) => {
    const c = context as { user?: { role: string } };
    if (c.user?.role !== 'ADMIN') throw redirect({ to: '/app' });
  },
  component: AdminPage,
});

const adminFeedbackRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'admin/feedback',
  beforeLoad: ({ context }) => {
    const c = context as { user?: { role: string } };
    if (c.user?.role !== 'ADMIN') throw redirect({ to: '/app' });
  },
  component: AdminFeedbackPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  joinRoute,
  groupInviteRoute,
  appLayoutRoute.addChildren([
    indexRoute,
    adminRoute,
    adminFeedbackRoute,
    membersRoute,
    contactsImportRoute,
    profileRoute,
    searchRoute,
    matchesRoute,
    mapRoute,
    settingsRoute,
    helpRoute,
    backupRoute,
    moreRoute,
    groupsRoute,
    groupDetailRoute,
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export { useAuth };
